<script setup lang="ts">
import model from '@/ts/model';
import type { SiteNotificationOptions } from '@/ts/types/notification-types';
import { computed, nextTick, ref, watch } from 'vue';
import Close from './close.vue';
import Btn from './btn.vue';
import AsyncLoad from './async-load.vue';
import { closeNotification } from '@/ts/management/notifications-management';

const visibleNotif = computed<SiteNotificationOptions | undefined>(() => model.notifications[0]);
const twitch = ref<boolean>(false);

watch(() => visibleNotif.value, msg => {
    if(!msg?.component) return;
    twitch.value = true;
});

watch(() => twitch.value, async value => {
    if(!value) return;
    await nextTick();
    twitch.value = false;
}, {immediate: true});

</script>

<template>
    <teleport to="body" defer>
        <template v-if="visibleNotif">
            <div class="notification-overlay" @click.prevent.stop="!visibleNotif.closeDisabled && !visibleNotif.overlayCloseDisabled && closeNotification()" :class="[{blur: model.globalLoaderShown}, visibleNotif.customClass]">
                <div class="notification" @click.stop :class="[visibleNotif.customClass]" v-mobile>
                    <template v-if="!visibleNotif.closeDisabled">
                        <close :on-close="closeNotification" :location="'notification'" />
                    </template>
                    <div class="inner">
                        <div class="header" v-if="visibleNotif.title" v-mobile>{{ visibleNotif.title }}</div>
                        <template v-if="visibleNotif.text">
                            <div class="content" v-html="visibleNotif.text"></div>
                        </template>
                        <template v-else-if="visibleNotif.component && !twitch">
                            <div class="component-wrapper" v-mobile>
                                <async-load
                                    :load="visibleNotif.component"
                                    :bindings="visibleNotif.componentBindings"
                                />
                            </div>
                        </template>
                        <div class="checkboxes">
                            <template v-for="btn in visibleNotif.buttons">
                                <template v-if="btn.checkbox">
                                    <async-load
                                        :load="() => import('./forms/form-input.vue')"
                                        :global="true"
                                        :bindings="{
                                            type: 'checkbox',
                                            text: btn.checkbox.text,
                                            input: btn.checkbox.checked,
                                            'onUpdate:input': (value: boolean) => btn.checkbox!.checked = value,
                                        }"
                                    />
                                </template>
                            </template>
                        </div>
                    </div>
                </div>
                <div class="buttons">
                    <template v-for="btn in visibleNotif.buttons">
                        <btn :skin="btn.skin || 'primary'" 
                            @click.prevent.stop="btn.onClick?.(); closeNotification();" 
                            :disabled="btn.checkbox ? !btn.checkbox.checked : false"
                        >{{ btn.text }}</btn>
                    </template>
                </div>
            </div>
        </template>
    </teleport>
</template>

<style lang="scss" scoped>
@use '@/scss/shared';
.notification{
    position: relative;
    max-width: 650px;
    width: 100%;
    border-radius: 15px;
    padding: 2px;
    background-color: #fff;
    @include shared.site() using($site-key){
        background: linear-gradient(shared.theme-color('comp3_4', $site-key), shared.theme-color('primary_3', $site-key));
    }
    &.optimove{
        width: auto !important;
        &[data-mobile]{
            max-width: 90dvw !important;
            & :deep(.bee-popup-row-content){
                max-width: 100% !important;
            }
        }
        & :deep(.close){
            @include shared.site() using($site-key){
                background-color: shared.theme-color('comp2_3', $site-key);
            }
        }
        @include shared.site() using($site-key){
            background: linear-gradient(shared.theme-color('comp2_3', $site-key), shared.theme-color('primary_3', $site-key));
        }
    }
    &-overlay{
        position: fixed;
        display: flex;
        width: 100%;
        height: 100%;
        z-index: 300;
        justify-content: center;
        align-items: center;
        top:0;
        left:0;
        transition: filter .25s, opacity .25s;
        padding: 10px;
        box-sizing: border-box;
        flex-direction: column;
        &.blur{
            // filter: blur(5px);
			opacity: .15;
        }
        & > .buttons{
            display: flex;
            padding-top: 20px;
        }
        background-color: #000000b9;
    }
    & > .inner{
        position: relative;
        border-radius: inherit;
        overflow: hidden;
        @include shared.site() using($site-key){
            background: linear-gradient(shared.theme-color('primary_0', $site-key) 25%, shared.theme-color('neutral_1', $site-key) 100%);
        }
        & > .header{
            display: flex;
            position: relative;
            @include shared.casino-section-title();
            font-size: 30px;
            height: 100px;
            justify-content: center;
            align-items: center;
            @include shared.site() using($site-key){
                color: shared.theme-color("primary_3", $site-key);
            }
            &[data-mobile]{
                font-size: 20px;
                height: 50px;
            }
        }
        & > .content{
            position: relative;
            padding: 30px;
            font-weight: 700;
            max-height: calc(100vh - 400px);
            margin-bottom: 50px;
            overflow-y: auto;
            @include shared.site() using($site-key){
                color: shared.theme-color("primary_3", $site-key);
            }
        }
        & > .component-wrapper{
            position: relative;
            border-radius: inherit;
            overflow: hidden;
            &[data-mobile]{
                max-height: calc(100dvh - 150px);
                overflow-y: auto;
            }
        }
        & .checkboxes{
            & :deep(.form-input){
                padding: 20px 0;
                & label > span{
                    @include shared.site() using($site-key){
                        color: shared.theme-color("primary_3", $site-key) !important;
                    }
                }
            }
        }
    }
}
</style>